import { useEffect, useState, type FormEvent } from "react";
import { Button } from "@/ui/Button";
import { Field } from "@/ui/Field";
import { Callout } from "@/ui/Callout";
import { ROLE_LABEL, Shell } from "@/app/Shell";
import { safeReturn } from "@/lib/safeReturn";

/** Тексты ошибок входа по правилам E1: причина и одно действие вперёд. */
export const LOGIN_ERRORS: Record<string, { title: string; body: string }> = {
  no_access: {
    title: "Этот аккаунт не приглашён в школу",
    body: "Вход только по приглашению: попросите администратора добавить вас — по Telegram или по почте, на которую придёт ссылка.",
  },
  telegram_invalid: {
    title: "Telegram не подтвердил вход",
    body: "Подпись устарела или не сошлась. Нажмите кнопку ещё раз — новая попытка займёт секунды.",
  },
  code_invalid: {
    title: "Ссылка для входа больше не действует",
    body: "Она открывается один раз и живёт 15 минут. Запросите новую — старую можно удалить.",
  },
};

/** Участник школы для dev-панели «войти как…» (GET /auth/dev-login, только APP_ENV=dev). */
export type DevAccount = { name: string; email: string; role: string };

export function LoginPage({ error, returnTo = "/" }: { error?: string; returnTo?: string }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "invalid" | "failed">("idle");
  const err = error ? LOGIN_ERRORS[error] : undefined;
  const [dev, setDev] = useState<DevAccount[] | null>(null);
  const [devPick, setDevPick] = useState("");
  const [devState, setDevState] = useState<"idle" | "sending" | "failed">("idle");

  useEffect(() => {
    let alive = true;
    fetch("/auth/dev-login")
      .then(async (r) => (r.ok ? ((await r.json()) as DevAccount[]) : null))
      .then((list) => {
        if (!alive || !list || list.length === 0) return;
        setDev(list);
        setDevPick(list[0]!.email);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const devLogin = async () => {
    setDevState("sending");
    try {
      const res = await fetch("/auth/dev-login", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: devPick }) });
      if (res.status !== 204) throw new Error(String(res.status));
      window.location.assign(safeReturn(returnTo));
    } catch {
      setDevState("failed");
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setState("sending");
    try {
      const res = await fetch("/auth/email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, return: returnTo }),
      });
      setState(res.status === 202 ? "sent" : res.status === 422 ? "invalid" : "failed");
    } catch {
      setState("failed");
    }
  };

  return (
    <Shell screen="вход">
      <div className="mx-auto flex w-full max-w-[420px] flex-col gap-6 pt-10">
        <div>
          <div className="mb-3 font-mono text-[11px] uppercase tracking-[.08em] text-accdim">менторская программа</div>
          <h1 className="m-0 font-mono text-[34px] font-bold leading-[1.1] tracking-[-.02em]">Вход</h1>
          <p className="mt-3 text-[15px] text-ink2 text-pretty">Доступ по приглашению. Войдите через Telegram — так бот сможет присылать уведомления о проверке.</p>
        </div>
        {err && (
          <Callout kind="err" tag="вход" title={err.title} collapsible={false}>
            {err.body}
          </Callout>
        )}
        <a href={`/auth/telegram/start?return=${encodeURIComponent(returnTo)}`} className="no-underline">
          <Button variant="primary" size="lg" className="w-full justify-center">
            Войти через Telegram
          </Button>
        </a>
        <div className="flex items-center gap-3 font-mono text-[11px] text-mut">
          <span className="h-px flex-1 bg-bd" />
          или по почте
          <span className="h-px flex-1 bg-bd" />
        </div>
        {state === "sent" ? (
          <Callout kind="ok" tag="письмо" title="Ссылка отправлена" collapsible={false}>
            Если адрес {email} известен школе, письмо уже идёт. Ссылка действует 15 минут и открывается один раз.
          </Callout>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-3">
            <Field
              label="почта"
              type="email"
              name="email"
              autoComplete="email"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              error={state === "invalid" ? "введите адрес в виде name@example.com" : state === "failed" ? "не удалось отправить — попробуйте ещё раз через минуту" : undefined}
            />
            <Button type="submit" variant="secondary" disabled={state === "sending"} reason={state === "sending" ? "отправляем…" : undefined}>
              Прислать ссылку для входа
            </Button>
          </form>
        )}
        {dev && (
          <section data-testid="dev-login" className="mt-2 rounded-md border border-dashed border-warn/60 bg-warnsoft p-4">
            <div className="mb-2 font-mono text-[11px] uppercase tracking-[.08em] text-warn">dev-вход · только в разработке</div>
            <p className="m-0 mb-3 text-[12.5px] leading-[1.5] text-ink2">Войти как любой участник школы из сида без Telegram и почты. В prod этой панели и маршрута нет.</p>
            <div className="flex flex-wrap items-center gap-2">
              <select aria-label="войти как" value={devPick} onChange={(e) => setDevPick(e.target.value)} className="min-w-0 flex-1 rounded-sm border border-bd bg-bg2 px-2.5 py-2 font-sans text-[13px] text-ink">
                {dev.map((a) => (
                  <option key={a.email} value={a.email}>
                    {a.name} · {ROLE_LABEL[a.role] ?? a.role} · {a.email}
                  </option>
                ))}
              </select>
              <Button variant="primary" onClick={() => void devLogin()} disabled={devState === "sending"} reason={devState === "sending" ? "входим…" : undefined}>
                войти
              </Button>
            </div>
            {devState === "failed" && <p className="m-0 mt-2 font-mono text-[11.5px] text-err">не удалось войти — этот пользователь не состоит в школе</p>}
          </section>
        )}
      </div>
    </Shell>
  );
}
